import { AfterViewInit, Component, Inject, OnDestroy, OnInit, Optional } from '@angular/core';
import { CurrentUserStateService, MenuItem, NotificationService, UserProfileService, MenuUtility, QUANGULAR_CONFIG, QuangularConfig, IToasterData} from 'quangular';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { filter, Subscription } from 'rxjs';
import { InteractionStatus } from '@azure/msal-browser';
import { UserProfileService as LocalUserProfileService } from '@app/modules/user-profile/services/user-profile.service';
import { DashboardService } from './modules/dashboard/services/dashboard.service';
import { IUser } from 'quangular/lib/models/user.model';
import { FeatureFlagService } from './services/feature-flag.service';
import { UserRole } from '@common/enums/UserRole';
import { FeatureFlag } from '@common/enums/FeatureFlag';
import { AppRoute } from './core/enums/app-route.enum';
import { NotificationActivityService } from './services/notification-activity.service';
import { SpinnerService } from './services/common/SpinnerService';
import { QuangularToasterService } from './services/common/toaster.service';
import { NavigationEnd, Router } from '@angular/router';
import { isUserInRole } from './services/common/utils';
import { menuItems } from './app-menu.config';
@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
    title = 'bo-quilt-app';
    private subscription: Subscription = new Subscription();
    loading = true;
    showNotificationTray: boolean = false;
    userInRole: boolean = false;
    isNotificationsEnabled: boolean = false;

    isDashboardEnabled: boolean = false;
    isTrarsheetPackageEnabled: boolean = false;
    menuItemData: MenuItem[] = [];
    toasterData: IToasterData;
    spinnerFlag: boolean = false;

    notificationLabel = 'View all notifications'
    notificationUrl = `/${AppRoute.Dashboard}/${AppRoute.AlertsWorkflow}?active=workflow-timeline&notified=true`;
    constructor(
        @Optional() private readonly notificationService: NotificationService,
        private readonly authService: MsalService,
        private readonly msalBroadcastService: MsalBroadcastService,
        private readonly userProfileService: UserProfileService,
        private readonly currentUserStateService: CurrentUserStateService,
        public readonly spinnerService: SpinnerService,
        private readonly featureFlagService: FeatureFlagService,
        private readonly notificationActivityService: NotificationActivityService,
        @Inject(QUANGULAR_CONFIG) private readonly config: QuangularConfig,
        private readonly appToaster: QuangularToasterService,
        public readonly router: Router,
        private readonly localUserProfileService: LocalUserProfileService,
        private readonly dashboardService: DashboardService
    ) {
        this.menuItemData = structuredClone(menuItems);
    }

    ngOnInit(): void {
        this.subscription.add(this.appToaster.toasterData$.subscribe(data => {
                this.toasterData = data;
        }));
        // Handle the redirect response on page load
        this.subscription.add(this.authService.handleRedirectObservable().subscribe({
            next: (result) => {
                // Handle successful sign in
                if (result) {
                    console.log('Login successful');
                }
            },
            error: (error) => {
                console.error('Login failed:', error);
            }
        }));

        // Monitor for login failures that need interaction
        this.subscription.add(this.msalBroadcastService.inProgress$
            .pipe(filter(status => status === InteractionStatus.None))
            .subscribe(async () => {
                // Check if we have accounts
                const accounts = this.authService.instance.getAllAccounts();
                if (accounts.length === 0) {
                    // No accounts, trigger login
                    this.localUserProfileService.clearMeCache();
                    this.dashboardService.clearTasksCache();
                    this.authService.loginRedirect();
                    this.setShowNotificationTray();
                }
                else {
                    await this.getUser();
                    this.loading = false;
                }
            }));

        // Spinner visibility flag
        this.subscription.add(this.spinnerService.visibility.subscribe((spinnerFlag) => {
            Promise.resolve().then(() => {
                this.spinnerFlag = spinnerFlag;
            });
        }));

        // Forcing light mode by removing data-theme attribute if exists, as the default theme is set to light in index.html
        document.documentElement.removeAttribute('data-theme');
    }

    ngAfterViewInit(): void {
        // Listen for route changes
        this.subscription.add(this.router.events
        .pipe(filter(event => event instanceof NavigationEnd))
        .subscribe((event: any) => {
            setTimeout(() => this.handleMenuHighlight(event.urlAfterRedirects), 100);
        }));
        this.getCurrentUser();
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    /**
     * Handle menu highlighting based on the current route
     * @param currentRoute
     * @returns
     */
    handleMenuHighlight(currentRoute: string) {
        const menuItemsDoc = document.querySelector('qds-main-toolbar') as any;
        const horizontalMenu = menuItemsDoc?.shadowRoot?.querySelector('qds-horizontal-menu');
        // get nav-list
        const navList = horizontalMenu?.shadowRoot.querySelector('.nav-list')

        if (!navList) return;

        // Remove active from ALL top-level items
        const topLevelLinks = navList.querySelectorAll('.top-level-item');
        topLevelLinks.forEach((el: any) => el.classList.remove('active'));

        // Find all submenu links
        const allLinks = navList.querySelectorAll('a');

        for (const link of allLinks) {
            const href = link.getAttribute('href');
            if (!href) continue;

            const modify = `/${href}`;
            const firstHref = modify.split('/')[1] || null;

            // Extract first segment of current route
            const currentBase = currentRoute.split('/')[1] || null;
            if (firstHref && currentBase && firstHref === currentBase) {
                const parentNavItem = link.closest('.nav-item');
                if (parentNavItem) {
                    const topMenu = parentNavItem.querySelector('.top-level-item');
                    topMenu?.classList.add('active');
                }

                // STOP here
                break;
            }
        }
    }

    private async getUser(): Promise<void> {
        this.subscription.add(
            this.userProfileService.getMe().subscribe({
                next: async (response: IUser) => {
                    this.currentUserStateService.init(response);
                },
            })
        );
    }

    private getCurrentUser() {
        // Get Current user
        this.subscription.add(this.currentUserStateService.value$.subscribe((value: any) => {
            const currentUser = value;
            if (currentUser) {
                this.userInRole = isUserInRole(currentUser?.roles, UserRole.Admin);

                // Admin Menu remove for other roles
                if (!this.userInRole) {
                    this.menuRemove(AppRoute.Admin);
                }

                this.setShowNotificationTray();
            }
        }));

        // Set Notifications flag Enabled
        this.subscription.add(this.featureFlagService.getFeatureFlag().subscribe(response => {
            if (response && response != null) {
                // Set isVisible value for dashboard
                this.featureFlagService.isEnabled(FeatureFlag.DASHBOARD).then(flag => {
                    this.isDashboardEnabled = flag;
                    if (!this.isDashboardEnabled) {
                        this.menuRemove(AppRoute.Dashboard);
                    }
                });

                // Set isVisible value for trarsheetpackage
                this.featureFlagService.isEnabled(FeatureFlag.TEARSHEETPACKAGE).then(flag => {
                    this.isTrarsheetPackageEnabled = flag;
                    if (!this.isTrarsheetPackageEnabled) {
                        this.menuRemove(AppRoute.Portfolio, AppRoute.Tearsheets);
                    }
                });

                // Set isVisible value for dashboard
                this.featureFlagService.isEnabled(FeatureFlag.NOTIFICATIONS).then(flag => {
                    this.isNotificationsEnabled = flag;
                    this.setShowNotificationTray();
                });
            }
        }));
    }

    private setShowNotificationTray() {
        this.showNotificationTray = this.isNotificationsEnabled;
        // Load notification
        if (this.showNotificationTray) {
            this.loadNotification();
        }
    }

    private loadNotification() {
        // Use setTimeout to ensure the web component is fully loaded and rendered
        setTimeout(() => {
            this.notificationService.setMoreLabel(this.notificationLabel);
            this.notificationService.setMoreUrl(this.notificationUrl);

            this.displayWelcomeNotification();
            this.getNotificationActivities();
            this.notifyCompletedActivities();
        }, 1000); // 1-second delay
    }

    /**
     * Displays a welcome notification to the user
     */
    private displayWelcomeNotification(): void {
        const currentUser = this.currentUserStateService.value;
        if (currentUser) {

            const username = currentUser.name || 'there';

            // Set up the notification tray configuration
            this.notificationService.setMoreLabel(this.notificationLabel);
            this.notificationService.setMoreUrl(this.notificationUrl);

            // Add a welcome notification
            this.notificationService.addNotification({
                title: 'Welcome to QUILT',
                message: `Welcome back, ${username}! You're now logged into QUILT.`,
                icon: '<i class="pi pi-check-circle"></i>',
                actionUrl: '/portfolio'
            });
        }
    }

    private getNotificationActivities(): void {
        const currentUser: any = this.currentUserStateService.value;
        if (currentUser) {
            this.subscription.add(this.notificationActivityService.getNotificaitionActivities(currentUser.email.toLocaleLowerCase()).subscribe((activities: any) => {
                for (const activity of activities) {
                    this.notificationService.addNotification({
                        title: activity.title,
                        message: activity.message,
                        timestamp: activity?.notificationDate ? new Date(activity.notificationDate) : null,
                        icon: activity.icon || '<i class="pi pi-info-circle"></i>',
                        actionUrl: activity.url || `/${AppRoute.Dashboard}/${AppRoute.AlertsWorkflow}?active=workflow-timeline`
                    });
                }
                this.notificationService.setMoreLabel(this.notificationLabel);
                this.notificationService.setMoreUrl(this.notificationUrl);
            }));
        }
    }

    private notifyCompletedActivities(): void {
        this.subscription.add(this.notificationActivityService.notifyCompletedEvents({}).subscribe(() => {}));
    }

    private menuRemove(linkName: string, subMenuName: string = null) {
        const index = this.menuItemData.findIndex(m => m.link == linkName);
        if (index > -1) {
            if (subMenuName) {
                const subIndex = this.menuItemData[index].items.findIndex(m => m.link == `${linkName}/${subMenuName}`);
                this.menuItemData[index].items.splice(subIndex, 1);
            } else {
                this.menuItemData.splice(index, 1);
            }
            this.getUserAvatarTag();
        }
    }

    private getUserAvatarTag() {
        const menuItemsDoc = document.querySelector('qds-main-toolbar') as any;
        if (menuItemsDoc) {
            // Convert to QDS format
            const qdsMenuItems = MenuUtility.quangular2Qds(this.menuItemData);
            menuItemsDoc.attributeChangedCallback('main-menu-data', JSON.stringify(this.config.mainMenu), JSON.stringify(qdsMenuItems));
        }
    }
}
